import { useMemo } from "react";

import { useAuth } from "@/contexts/auth-context";
import { useOpsBrands } from "@/hooks/use-brands";

/** Who is viewing and whose brand the record is: what names our side of a conversation. */
export interface OurSide {
  viewerEmail: string | null;
  viewerName: string | null;
  brand: string | null;
}

/** The brand that names our side of a person's history: the one brand every message is from (the record's own when
 *  the messages carry none). A person's history can span brands, and one brand would then name the wrong team, so a
 *  mixed history gets null and its rows say "Our team". */
export function ourBrandId(contacts: { tenant_id?: string | null }[], fallback: string | null): string | null {
  const ids = new Set(contacts.map((c) => c.tenant_id).filter(Boolean));
  if (ids.size > 1) return null;
  return [...ids][0] ?? fallback;
}

/** Our side of a record's conversations (the viewer and the brand), so a timeline row names both ends the same way on
 *  every record surface: "Bell → Daniel & Rosa Keane". `brandId` null names no brand. */
export function useOurSide(brandId: string | null): OurSide {
  const { user } = useAuth();
  const { data: brands } = useOpsBrands();
  const viewerEmail = user?.email ?? null;
  const viewerName = user?.display_name ?? null;
  const brand = brandId ? (brands?.find((b) => b.id === brandId)?.short_name ?? null) : null;
  return useMemo(() => ({ viewerEmail, viewerName, brand }), [viewerEmail, viewerName, brand]);
}
